/**
 * The recovery-banner store (`112-offline-drafts.md` §7.3, §7.4).
 *
 * A conflict discovered on reload (§7.3) and a `412 stale_write` discovered by an outbox
 * replay (§7.4) are the SAME situation found at different moments, so they take the same
 * path and open the same banner. There is deliberately only one conflict UI in this app.
 *
 * A tiny external store rather than React context: the outbox is not a component and
 * cannot dispatch into one, and the banner must be reachable from the sync indicator in
 * the top bar as well as from the editor.
 */
import type { PageDto } from '@/api/endpoints/pages';
import type { DraftRecord } from '@/offline/db';

export interface Conflict {
  pageId: string;
  draft: DraftRecord;
  server: PageDto;
  /** The failed outbox entry, when the conflict came from a 412 replay (§7.4). */
  entryId?: number;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let conflicts: ReadonlyMap<string, Conflict> = new Map();

function emit(next: ReadonlyMap<string, Conflict>): void {
  conflicts = next;
  for (const listener of listeners) listener();
}

export function subscribeConflicts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getConflicts(): ReadonlyMap<string, Conflict> {
  return conflicts;
}

export function openConflict(conflict: Conflict): void {
  const next = new Map(conflicts);
  next.set(conflict.pageId, conflict);
  emit(next);
}

/**
 * Only ever called by one of the banner's three buttons. The banner is NOT dismissible —
 * `Esc` does not close it — because a conflict the user "closes" without resolving is a
 * conflict that quietly resurfaces as data loss the next time the autosave fires.
 */
export function resolveConflict(pageId: string): void {
  if (!conflicts.has(pageId)) return;
  const next = new Map(conflicts);
  next.delete(pageId);
  emit(next);
}
